// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type {
  Condition,
  Coverage,
  Encounter,
  MedicationRequest,
  Observation,
  Patient,
  Practitioner,
  Resource,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { hasIcd10Coding, isAhiObservation } from '../utils/dme-task';
import { fetchPrimaryCoverage } from '../utils/patient-coverage';

export interface DmeTaskDetails {
  medicationRequest: MedicationRequest | undefined;
  encounter: Encounter | undefined;
  patient: Patient | undefined;
  orderingProvider: Practitioner | undefined;
  coverage: Coverage | undefined;
  diagnosis: Condition | undefined;
  ahiObservation: Observation | undefined;
}

export interface UseDmeTaskDetailsResult {
  details: DmeTaskDetails;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

const EMPTY_DETAILS: DmeTaskDetails = {
  medicationRequest: undefined,
  encounter: undefined,
  patient: undefined,
  orderingProvider: undefined,
  coverage: undefined,
  diagnosis: undefined,
  ahiObservation: undefined,
};

async function readReferenceOfType<T extends Resource>(
  medplum: ReturnType<typeof useMedplum>,
  reference: string | undefined,
  resourceType: T['resourceType']
): Promise<T | undefined> {
  if (!reference?.startsWith(`${resourceType}/`)) {
    return undefined;
  }
  try {
    return await medplum.readReference<T>({ reference });
  } catch {
    return undefined;
  }
}

/**
 * Loads every resource a submit-dme-order Task detail view needs: the linked
 * Treatment (focus), Encounter, Patient, ordering Practitioner (owner), the
 * patient's primary Coverage, and the encounter's OSA diagnosis Condition + AHI
 * Observation. Each lookup degrades to `undefined` rather than failing the whole
 * load, so a missing diagnosis never blanks the rest of the view.
 */
export function useDmeTaskDetails(task: Task | undefined): UseDmeTaskDetailsResult {
  const medplum = useMedplum();
  const [details, setDetails] = useState<DmeTaskDetails>(EMPTY_DETAILS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!task) {
      setDetails(EMPTY_DETAILS);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [medicationRequest, encounter, patient, orderingProvider] = await Promise.all([
        readReferenceOfType<MedicationRequest>(medplum, task.focus?.reference, 'MedicationRequest'),
        readReferenceOfType<Encounter>(medplum, task.encounter?.reference, 'Encounter'),
        readReferenceOfType<Patient>(medplum, task.for?.reference, 'Patient'),
        readReferenceOfType<Practitioner>(medplum, task.owner?.reference, 'Practitioner'),
      ]);

      const [coverage, encounterConditions, encounterObservations] = await Promise.all([
        patient?.id ? fetchPrimaryCoverage(medplum, patient.id) : Promise.resolve(undefined),
        encounter?.id
          ? medplum.searchResources('Condition', { encounter: `Encounter/${encounter.id}` })
          : Promise.resolve([]),
        encounter?.id
          ? medplum.searchResources('Observation', { encounter: `Encounter/${encounter.id}` })
          : Promise.resolve([]),
      ]);

      const diagnosis = Array.from(encounterConditions).find(hasIcd10Coding);
      const ahiObservation = Array.from(encounterObservations).find(isAhiObservation);

      setDetails({
        medicationRequest,
        encounter,
        patient,
        orderingProvider,
        coverage,
        diagnosis,
        ahiObservation,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load DME task details'));
    } finally {
      setLoading(false);
    }
  }, [medplum, task]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { details, loading, error, refresh: fetch };
}
